<script setup lang="ts">
// Settings › Devices: list/name/remove devices, link a new one (QR), manage the recovery code.
// Device names live only on my devices (synced through my self-group), never on the server.
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import QRCode from 'qrcode'
import { describeError } from '../api/http'
import type { LinkTokenResponse, MyDevice } from '../api/types'
import { formatBytes } from '../lib/media'
import { linkWithName } from '../lib/metacrypto'
import { estimate, newTransferKey, sendHistory, stopTransfer, transfer, transferRunning } from '../lib/transfer'
import { useSession } from '../stores/session'

const session = useSession()
const devices = ref<MyDevice[]>([])
const names = ref<Record<string, string>>({})
const link = ref<LinkTokenResponse | null>(null)
// The name for the device about to be linked; null when not asking.
const newName = ref<string | null>(null)
const newNameInput = ref<HTMLInputElement | null>(null)
// Whether to send the new device my message history (HRTH-202), and attachments with it.
const withHistory = ref(true)
const withAttachments = ref(false)
const kept = ref<{ attachments: number; bytes: number } | null>(null)
// The device being renamed, and the name being typed.
const renaming = ref<string | null>(null)
const renameTo = ref('')
const qr = ref<string | null>(null)
const remaining = ref(0)
const recoveryEnabled = ref<boolean | null>(null)
const newCode = ref<string | null>(null)
const busy = ref(false)
let timer: ReturnType<typeof setInterval> | null = null

async function load(): Promise<void> {
  names.value = session.engine?.deviceNamesById() ?? {}
  devices.value = (await session.api.myDevices()).devices
  recoveryEnabled.value = (await session.api.recoveryStatus()).enabled
}

const unsubscribe = session.engine?.on((e) => {
  if (e.type === 'device_names') names.value = session.engine?.deviceNamesById() ?? {}
})
onMounted(() => load().catch((e) => session.toast(describeError(e), 'error')))
onUnmounted(() => {
  if (timer) clearInterval(timer)
  unsubscribe?.()
})

function deviceLabel(d: MyDevice): string {
  return names.value[d.device_id] ?? (d.current ? 'This device' : 'Unnamed device')
}

async function askName(): Promise<void> {
  newName.value = ''
  withHistory.value = true
  withAttachments.value = false
  kept.value = null
  void estimate(session.transferSource()).then((e) => (kept.value = e), () => undefined)
  await nextTick()
  newNameInput.value?.focus()
}

async function startLink(): Promise<void> {
  const name = newName.value?.trim()
  if (!name) return
  busy.value = true
  try {
    // The name goes in the link's fragment, for the new device only: the server never sees it.
    // So does the history transfer's key, when asked for.
    const t = await session.api.createLinkToken()
    const key = withHistory.value ? newTransferKey() : null
    const from = session.engine!.deviceId
    link.value = { ...t, url: linkWithName(t.url, name, key ? { key, from } : undefined) }
    linkedSince = Date.now()
    if (key) void sendHistory(session.api, key, session.transferSource(), { attachments: withAttachments.value })
    newName.value = null
    qr.value = await QRCode.toDataURL(link.value.url, { margin: 1, width: 220 })
    const tick = () => {
      remaining.value = Math.max(0, Math.round(((link.value?.expires_at ?? 0) - Date.now()) / 1000))
      if (remaining.value === 0) expire()
    }
    tick()
    timer = setInterval(() => {
      tick()
      // Refresh the device list so the new device shows up once linked.
      void load()
    }, 3000)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

let linkedSince = 0

/** The link ran out: a transfer carries on if a device linked with it, else it's stopped. */
function expire(): void {
  if (transfer.role === 'send' && transfer.phase === 'waiting' && !devices.value.some((d) => d.created_at >= linkedSince)) stopTransfer()
  cancelLink()
}

/** Done: closes the link, asking first if history is still being sent. */
function done(): void {
  if (transfer.role === 'send' && transferRunning()) {
    if (!confirm('Your message history is still being sent to the new device. Stop sending it?')) return
    stopTransfer()
  }
  cancelLink()
}

function cancelLink(): void {
  link.value = null
  qr.value = null
  if (timer) clearInterval(timer)
  timer = null
}

function startRename(d: MyDevice): void {
  renaming.value = d.device_id
  renameTo.value = names.value[d.device_id] ?? ''
}

async function saveRename(): Promise<void> {
  const id = renaming.value
  const name = renameTo.value.trim()
  if (!id || !name) return
  try {
    await session.engine!.renameDevice(id, name)
    renaming.value = null
    names.value = session.engine!.deviceNamesById()
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function remove(d: MyDevice): Promise<void> {
  if (!confirm('Remove this device? It will be signed out and lose access to everything.')) return
  try {
    await session.api.removeDevice(d.device_id)
    await load()
    await session.refresh()
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function makeCode(): Promise<void> {
  if (recoveryEnabled.value && !confirm('Create a new recovery code? The old one will stop working.')) return
  busy.value = true
  try {
    newCode.value = await session.engine!.enableRecovery()
    recoveryEnabled.value = true
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

/** The device link was just copied (the button says so for a moment). */
const linkCopied = ref(false)
async function copyLink(e?: Event): Promise<void> {
  ;(e?.target as HTMLInputElement | undefined)?.select?.()
  if (!link.value) return
  try {
    await navigator.clipboard.writeText(link.value.url)
    linkCopied.value = true
    setTimeout(() => (linkCopied.value = false), 2000)
  } catch {
    // Clipboard blocked (e.g. insecure context): the link is selected to copy by hand.
  }
}

async function copyCode(): Promise<void> {
  try {
    await navigator.clipboard.writeText(newCode.value ?? '')
    session.toast('Recovery code copied')
  } catch {
    // selectable fallback
  }
}
</script>

<template>
  <section class="space-y-3">
    <div class="label">Your devices</div>
    <ul class="border-t border-border" data-testid="device-list">
      <li
        v-for="d in devices"
        :key="d.device_id"
        class="flex items-center gap-3 border-b border-border py-2 text-sm"
      >
        <span class="mono w-8 text-text-disabled">{{ d.current ? '[*]' : '[ ]' }}</span>
        <form v-if="renaming === d.device_id" class="flex min-w-0 flex-1 gap-2" @submit.prevent="saveRename">
          <input
            v-model="renameTo"
            class="input"
            maxlength="64"
            aria-label="Device name"
            data-testid="device-rename-input"
            @keydown.esc.stop="renaming = null"
          />
          <button class="btn-primary text-xs" :disabled="!renameTo.trim()">Save</button>
          <button type="button" class="btn-ghost text-xs" @click="renaming = null">Cancel</button>
        </form>
        <template v-else>
          <div class="min-w-0 flex-1">
            <div class="truncate text-text-secondary" data-testid="device-name">
              {{ deviceLabel(d) }}<span v-if="d.current && names[d.device_id]" class="text-text-muted"> · this device</span>
            </div>
            <div class="mono text-text-muted">added {{ new Date(d.created_at).toLocaleDateString() }}</div>
          </div>
          <button class="mono text-text-muted uppercase hover:underline" data-testid="device-rename" @click="startRename(d)">
            {{ names[d.device_id] ? 'Rename' : 'Name' }}
          </button>
          <button v-if="!d.current" class="mono text-error uppercase hover:underline" @click="remove(d)">Remove</button>
        </template>
      </li>
    </ul>

    <div v-if="link" class="border border-border p-3 text-center" data-testid="link-panel">
      <p class="mb-2 text-sm text-text-secondary">Scan with your other device, or open this link there:</p>
      <img v-if="qr" :src="qr" alt="QR code for linking a device" class="mx-auto bg-white p-1" />
      <div class="mt-2 flex gap-2">
        <input
          class="input mono min-w-0 flex-1 cursor-pointer"
          :value="link.url"
          readonly
          title="Click to copy"
          aria-label="Link for the new device"
          data-testid="link-url"
          @click="copyLink"
        />
        <button type="button" class="btn-primary shrink-0" data-testid="copy-link" @click="copyLink()">{{ linkCopied ? 'Copied!' : 'Copy' }}</button>
      </div>
      <p class="mono mt-1 text-text-muted">Expires in {{ Math.floor(remaining / 60) }}:{{ String(remaining % 60).padStart(2, '0') }}</p>
      <button class="btn-ghost mt-1 text-xs" data-testid="link-done" @click="done">Done</button>
    </div>
    <div v-if="transfer.role === 'send' && transfer.phase !== 'stopped'" class="border border-border p-3 text-sm" data-testid="transfer-send">
      <p v-if="transfer.phase === 'waiting'" class="text-text-secondary">Your message history will be sent once the new device is set up.</p>
      <template v-else-if="transfer.phase === 'history' || transfer.phase === 'attachments'">
        <p class="text-text-secondary">
          Sending your message history… {{ transfer.total ? Math.floor((100 * transfer.done) / transfer.total) : 0 }}%
        </p>
        <p class="mono text-text-muted">Keep this open until it’s done.</p>
      </template>
      <p v-else-if="transfer.phase === 'done'" class="text-text-secondary">
        Sent {{ transfer.messages }} message{{ transfer.messages === 1 ? '' : 's' }}<template v-if="transfer.attachments">
          and {{ transfer.attachments }} attachment{{ transfer.attachments === 1 ? '' : 's' }}</template> to the new device.
      </p>
      <p v-else class="text-error">{{ transfer.error }}</p>
      <button v-if="transferRunning()" class="btn-ghost mt-1 text-xs" @click="stopTransfer">Stop</button>
    </div>
    <form v-else-if="newName !== null" class="space-y-2 border border-border p-3" @submit.prevent="startLink">
      <label for="new-device-name" class="text-sm text-text-secondary">Name the new device</label>
      <input
        id="new-device-name"
        ref="newNameInput"
        v-model="newName"
        class="input"
        maxlength="64"
        placeholder="e.g. Work laptop"
        data-testid="new-device-name"
        @keydown.esc.stop="newName = null"
      />
      <label class="flex items-center gap-2 text-sm text-text-secondary">
        <input v-model="withHistory" type="checkbox" data-testid="transfer-history" />
        Transfer message history
      </label>
      <label class="flex items-center gap-2 text-sm" :class="withHistory ? 'text-text-secondary' : 'text-text-muted'">
        <input v-model="withAttachments" type="checkbox" :disabled="!withHistory" data-testid="transfer-attachments" />
        Also transfer attachments
        <span v-if="kept" class="mono text-text-muted">
          ({{ kept.attachments ? `about ${formatBytes(kept.bytes)}` : 'none on this device' }})
        </span>
      </label>
      <p v-if="withHistory" class="text-xs text-text-muted">
        Sent end-to-end encrypted, straight to the new device, once it’s set up. Keep this device open until then.
      </p>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-ghost text-xs" @click="newName = null">Cancel</button>
        <button class="btn-primary text-xs" :disabled="busy || !newName.trim()" data-testid="create-device-link">Create link</button>
      </div>
    </form>
    <button v-else class="btn-ghost w-full" data-testid="link-new-device" @click="askName">
      + Link a new device
    </button>

    <div class="label mt-4">Recovery code</div>
    <div v-if="newCode" class="border border-signal p-3" data-testid="recovery-panel">
      <p class="mb-2 text-xs text-text-secondary">
        Write this down and keep it somewhere safe. It’s the only way back into your account if you lose all your
        devices. It won’t be shown again.
      </p>
      <code class="block font-mono text-sm tracking-wider break-all text-text-primary select-all" data-testid="recovery-code">{{ newCode }}</code>
      <div class="mt-2 flex gap-2">
        <button class="btn-ghost text-xs" @click="copyCode">Copy</button>
        <button class="btn-primary text-xs" data-testid="recovery-saved" @click="newCode = null">I’ve saved it</button>
      </div>
    </div>
    <div v-else class="flex items-center justify-between gap-2 text-sm">
      <span class="mono uppercase" :class="recoveryEnabled === false ? 'text-signal' : recoveryEnabled ? 'text-text-primary' : 'text-text-muted'">
        {{ recoveryEnabled === null ? '…' : recoveryEnabled ? 'Set up' : 'Not set up — set one up!' }}
      </span>
      <button class="btn-ghost text-xs" :disabled="busy" data-testid="make-recovery-code" @click="makeCode">
        {{ recoveryEnabled ? 'Replace code' : 'Create code' }}
      </button>
    </div>
  </section>
</template>
